/**
 * 同意書エディター画面 (v3.2.26)。
 *
 * 構成:
 *   - ヘッダー: 戻る / タイトル / 保存 / 公開トグル / 削除
 *   - タブ: [編集] [公開リンクリスト]
 *   - [編集]: 2 カラム (左: プレビュー、右: 編集フォーム)
 *   - [公開リンクリスト]: 同意書テンプレに紐づく公開リンク + 手動発行ボタン
 */
import type {
  ConsentDocumentStatus,
  ConsentImagePosition,
  ConsentSection,
  ConsentSectionType,
} from '@goenhub/shared';
import {
  ArrowLeft,
  FileText,
  Globe,
  Link2,
  Loader2,
  Pencil,
  Plus,
  Save,
  Sparkles,
  Trash2,
  Upload,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import {
  useConsentDocument,
  useDeleteConsentDocument,
  useImportConsentDocumentFromPdf,
  useUpsertConsentDocument,
} from '@/hooks/useConsentDocuments';
import { useProductCategories } from '@/hooks/useProductCategories';
import { ImageFieldEditor } from '@/pages/landings/components/ImageFieldEditor';
import { paths } from '@/routes/paths';

import { ConsentSectionEditor } from './components/ConsentSectionEditor';
import { ConsentEditorPreview } from './components/ConsentEditorPreview';
import { ConsentLinksTab } from './components/ConsentLinksTab';
import {
  CONSENT_SECTION_DEFS,
  CONSENT_SECTION_ORDER,
  makeSection,
} from './sectionDefs';

const STATUS_LABEL: Record<ConsentDocumentStatus, string> = {
  draft: '下書き',
  published: '公開中',
  archived: 'アーカイブ',
};

const POSITION_OPTIONS: Array<{ value: ConsentImagePosition; label: string }> = [
  { value: 'top-left', label: '左上' },
  { value: 'top-center', label: '中央上' },
  { value: 'top-right', label: '右上' },
  { value: 'bottom-left', label: '左下' },
  { value: 'bottom-center', label: '中央下' },
  { value: 'bottom-right', label: '右下' },
];

export function ConsentDocumentEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: document, isLoading } = useConsentDocument(id);
  const upsert = useUpsertConsentDocument();
  const del = useDeleteConsentDocument();
  const importPdf = useImportConsentDocumentFromPdf();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { data: categories } = useProductCategories({ includeInactive: true });

  const [tab, setTab] = useState<'edit' | 'links'>('edit');

  const [title, setTitle] = useState('');
  const [status, setStatus] = useState<ConsentDocumentStatus>('draft');
  const [primaryCategoryId, setPrimaryCategoryId] = useState<string>('');
  const [sections, setSections] = useState<ConsentSection[]>([]);

  const [logoStoragePath, setLogoStoragePath] = useState<string | null>(null);
  const [logoPosition, setLogoPosition] =
    useState<ConsentImagePosition>('top-right');
  const [stampStoragePath, setStampStoragePath] = useState<string | null>(null);
  const [stampPosition, setStampPosition] =
    useState<ConsentImagePosition>('bottom-right');
  const [stampHidden, setStampHidden] = useState(false);

  const [showAddMenu, setShowAddMenu] = useState(false);

  useEffect(() => {
    if (document) {
      setTitle(document.title);
      setStatus(document.status);
      setPrimaryCategoryId(document.primaryCategoryId ?? '');
      setSections(document.sections ?? []);
      setLogoStoragePath(document.logoStoragePath ?? null);
      setLogoPosition(document.logoPosition ?? 'top-right');
      setStampStoragePath(document.stampStoragePath ?? null);
      setStampPosition(document.stampPosition ?? 'bottom-right');
      setStampHidden(Boolean(document.stampHidden));
    }
  }, [document]);

  // 必ず signature セクションが末尾にあることを表示上でも保証
  const sectionsWithSignature = useMemo(() => {
    const nonSig = sections.filter((s) => s.type !== 'signature');
    const sig = sections.find((s) => s.type === 'signature');
    if (sig) return [...nonSig, sig];
    return [...nonSig, makeSection('signature')];
  }, [sections]);

  const handleSave = async () => {
    if (!id) return;
    if (!title.trim()) {
      toast.error('タイトルは必須です');
      return;
    }
    try {
      await upsert.mutateAsync({
        id,
        title: title.trim(),
        status,
        primaryCategoryId: primaryCategoryId || null,
        sections: sectionsWithSignature,
        logoStoragePath,
        logoPosition: logoStoragePath ? logoPosition : null,
        stampStoragePath,
        stampPosition: stampStoragePath ? stampPosition : null,
        stampHidden,
      });
      toast.success('保存しました');
    } catch (e) {
      toast.error(`保存失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handleDelete = async () => {
    if (!document || !id) return;
    if (!confirm(`同意書「${document.title}」を削除します。よろしいですか？`))
      return;
    try {
      await del.mutateAsync(id);
      toast.success('削除しました');
      navigate(paths.consentDocuments);
    } catch (e) {
      toast.error(`削除失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handlePublishToggle = async () => {
    if (!id) return;
    const next: ConsentDocumentStatus =
      status === 'published' ? 'draft' : 'published';
    if (next === 'draft' && !confirm('公開を取り消して下書きに戻しますか？'))
      return;
    setStatus(next);
    try {
      await upsert.mutateAsync({
        id,
        title: title.trim(),
        status: next,
        primaryCategoryId: primaryCategoryId || null,
        sections: sectionsWithSignature,
        logoStoragePath,
        logoPosition: logoStoragePath ? logoPosition : null,
        stampStoragePath,
        stampPosition: stampStoragePath ? stampPosition : null,
        stampHidden,
      });
      toast.success(next === 'published' ? '公開しました' : '公開を取り消しました');
    } catch (e) {
      setStatus(status);
      toast.error(`変更失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handlePdfImport = async (file: File) => {
    if (file.size > 8 * 1024 * 1024) {
      toast.error('PDF は 8MB 以下にしてください');
      return;
    }
    if (file.type !== 'application/pdf') {
      toast.error('PDF ファイルを選択してください');
      return;
    }
    if (
      sections.filter((s) => s.type !== 'signature').length > 0 &&
      !confirm(
        '既存のセクションがあります。PDF から抽出したセクションを末尾に追加しますか？\n（既存セクションは保持されます）',
      )
    ) {
      return;
    }
    try {
      const b64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          if (typeof result !== 'string') {
            reject(new Error('PDF の読込結果が想定外'));
            return;
          }
          const idx = result.indexOf(',');
          resolve(idx >= 0 ? result.slice(idx + 1) : result);
        };
        reader.onerror = () => reject(reader.error ?? new Error('PDF 読込失敗'));
        reader.readAsDataURL(file);
      });
      const r = await importPdf.mutateAsync({
        pdfBase64: b64,
        pdfName: file.name,
      });
      setSections((prev) => {
        const sig = prev.find((s) => s.type === 'signature');
        const nonSig = prev.filter((s) => s.type !== 'signature');
        return sig ? [...nonSig, ...r.sections, sig] : [...nonSig, ...r.sections];
      });
      toast.success(`${r.sections.length} 件のセクションを抽出しました`);
    } catch (e) {
      toast.error(
        `PDF インポート失敗: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  };

  const handleAddSection = (type: ConsentSectionType) => {
    const newSec = makeSection(type);
    setSections((prev) => {
      const nonSig = prev.filter((s) => s.type !== 'signature');
      const sig = prev.find((s) => s.type === 'signature');
      const next = [...nonSig, newSec];
      if (sig) next.push(sig);
      return next;
    });
    setShowAddMenu(false);
  };

  const updateSection = (idx: number, next: ConsentSection) => {
    setSections((prev) => {
      const out = [...prev];
      const targetId = sectionsWithSignature[idx]?.id;
      const realIdx = out.findIndex((s) => s.id === targetId);
      if (realIdx >= 0) out[realIdx] = next;
      else out.push(next);
      return out;
    });
  };

  const deleteSection = (idx: number) => {
    const targetId = sectionsWithSignature[idx]?.id;
    setSections((prev) => prev.filter((s) => s.id !== targetId));
  };

  const moveSection = (idx: number, delta: -1 | 1) => {
    setSections((prev) => {
      const sig = prev.find((s) => s.type === 'signature');
      const nonSig = prev.filter((s) => s.type !== 'signature');
      const targetId = sectionsWithSignature[idx]?.id;
      const j = nonSig.findIndex((s) => s.id === targetId);
      const newIdx = j + delta;
      if (j < 0 || newIdx < 0 || newIdx >= nonSig.length) return prev;
      const reordered = [...nonSig];
      const [moved] = reordered.splice(j, 1);
      reordered.splice(newIdx, 0, moved);
      return sig ? [...reordered, sig] : reordered;
    });
  };

  if (isLoading || !document || !id) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner />
      </div>
    );
  }

  const isPublished = status === 'published';

  return (
    <div className="flex flex-col h-screen bg-[var(--bg-root)]">
      {/* ヘッダー */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--border)] bg-[var(--bg-elevated)]">
        <div className="flex items-center gap-2 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(paths.consentDocuments)}
          >
            <ArrowLeft className="size-4" />
          </Button>
          <h1 className="text-sm font-semibold truncate">{document.title}</h1>
          <Badge variant={isPublished ? 'accent' : 'draft'}>
            {STATUS_LABEL[status]}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="primary"
            onClick={() => void handleSave()}
            disabled={upsert.isPending}
          >
            {upsert.isPending ? (
              <Loader2 className="size-3.5 mr-1 animate-spin" />
            ) : (
              <Save className="size-3.5 mr-1" />
            )}
            保存
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void handlePublishToggle()}
            disabled={upsert.isPending}
          >
            <Globe className="size-3.5 mr-1" />
            {isPublished ? '公開停止' : '公開'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void handleDelete()}
            disabled={del.isPending}
            title="この同意書を削除"
            className="text-[var(--text-muted)] hover:text-red-500"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>

      {/* タブメニュー */}
      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as 'edit' | 'links')}
        className="flex flex-col flex-1 overflow-hidden"
      >
        <div className="px-4 pt-2 border-b border-[var(--border)] bg-[var(--bg-elevated)]">
          <TabsList>
            <TabsTrigger value="edit">
              <Pencil className="size-3.5 mr-1" />
              編集
            </TabsTrigger>
            <TabsTrigger value="links">
              <Link2 className="size-3.5 mr-1" />
              公開リンクリスト
            </TabsTrigger>
          </TabsList>
        </div>

        {/* 編集タブ — 2 カラム (左: 編集、右: プレビュー) */}
        <TabsContent
          value="edit"
          className="flex-1 overflow-hidden flex flex-col"
        >
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-0 overflow-hidden">
            {/* 左カラム: 編集フォーム */}
            <div className="overflow-y-auto bg-[var(--bg-elevated)] border-r border-[var(--border)] p-4">
              <div className="space-y-5 max-w-2xl">
                {/* メタ */}
                <section className="space-y-3">
                  <div>
                    <Label htmlFor="cd-title">タイトル</Label>
                    <Input
                      id="cd-title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="例: TimeWaver 利用同意書"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cd-category">主軸カテゴリー (任意)</Label>
                    <select
                      id="cd-category"
                      value={primaryCategoryId}
                      onChange={(e) => setPrimaryCategoryId(e.target.value)}
                      className="w-full px-2 py-1.5 rounded-sm border border-[var(--border)] bg-[var(--bg-input)] text-sm"
                    >
                      <option value="">未設定 (手動選択でのみ利用)</option>
                      {(categories ?? []).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                          {c.requiresConsent ? ' (同意ステップ有)' : ''}
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-[var(--text-muted)] mt-1">
                      商談の主軸カテゴリーがこれと一致する同意書が、自動送信時の候補になります。
                    </p>
                  </div>
                </section>

                {/* ロゴ・印鑑 */}
                <section className="rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] p-3 space-y-3">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <FileText className="size-4" />
                    ロゴ・印鑑
                  </h3>

                  <div className="space-y-3">
                    <div>
                      <ImageFieldEditor
                        label="ロゴ画像"
                        value={logoStoragePath}
                        onChange={setLogoStoragePath}
                        storagePath="consent-documents/imgs"
                      />
                      {logoStoragePath && (
                        <div className="mt-2">
                          <Label className="text-xs">ロゴ表示位置</Label>
                          <select
                            value={logoPosition}
                            onChange={(e) =>
                              setLogoPosition(
                                e.target.value as ConsentImagePosition,
                              )
                            }
                            className="w-full px-2 py-1 rounded-sm border border-[var(--border)] bg-[var(--bg-input)] text-xs"
                          >
                            {POSITION_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    <div>
                      <ImageFieldEditor
                        label="会社印鑑画像"
                        value={stampStoragePath}
                        onChange={setStampStoragePath}
                        storagePath="consent-documents/imgs"
                      />
                      {stampStoragePath && (
                        <>
                          <div className="mt-2">
                            <Label className="text-xs">印鑑表示位置</Label>
                            <select
                              value={stampPosition}
                              onChange={(e) =>
                                setStampPosition(
                                  e.target.value as ConsentImagePosition,
                                )
                              }
                              className="w-full px-2 py-1 rounded-sm border border-[var(--border)] bg-[var(--bg-input)] text-xs"
                            >
                              {POSITION_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <label className="mt-2 flex items-center gap-2 text-xs cursor-pointer">
                            <input
                              type="checkbox"
                              checked={stampHidden}
                              onChange={(e) => setStampHidden(e.target.checked)}
                            />
                            印鑑なし版で送付（公開画面に非表示）
                          </label>
                        </>
                      )}
                    </div>
                  </div>
                </section>

                {/* セクション */}
                <section className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold">セクション</h3>
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) void handlePdfImport(f);
                          e.target.value = '';
                        }}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={importPdf.isPending}
                        title="既存の同意書 PDF を AI で読み込み"
                      >
                        {importPdf.isPending ? (
                          <>
                            <Loader2 className="size-3.5 mr-1 animate-spin" />
                            AI 抽出中…
                          </>
                        ) : (
                          <>
                            <Sparkles className="size-3.5 mr-1" />
                            <Upload className="size-3.5 mr-1" />
                            PDF から AI 抽出
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {sectionsWithSignature.map((sec, idx) => (
                      <ConsentSectionEditor
                        key={sec.id}
                        section={sec}
                        index={idx}
                        total={sectionsWithSignature.length}
                        onChange={(next) => updateSection(idx, next)}
                        onDelete={() => deleteSection(idx)}
                        onMoveUp={() => moveSection(idx, -1)}
                        onMoveDown={() => moveSection(idx, 1)}
                      />
                    ))}
                  </div>

                  {/* セクション追加 */}
                  <div className="relative">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setShowAddMenu((v) => !v)}
                      className="w-full"
                    >
                      <Plus className="size-4 mr-1" />
                      セクションを追加
                    </Button>
                    {showAddMenu && (
                      <div className="absolute z-10 left-0 right-0 mt-1 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] shadow-lg p-1">
                        {CONSENT_SECTION_ORDER.map((t) => {
                          const def = CONSENT_SECTION_DEFS[t];
                          const Icon = def.icon;
                          return (
                            <button
                              key={t}
                              type="button"
                              onClick={() => handleAddSection(t)}
                              className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-xs hover:bg-[var(--bg-hover)]"
                            >
                              <Icon className="size-3.5 text-[var(--text-muted)]" />
                              <span className="font-medium">{def.label}</span>
                              <span className="text-[10px] text-[var(--text-muted)] ml-auto">
                                {def.description}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </div>

            {/* 右カラム: プレビュー */}
            <div className="overflow-y-auto bg-[var(--bg-root)] p-4">
              <ConsentEditorPreview
                title={title}
                sections={sectionsWithSignature}
                logoStoragePath={logoStoragePath}
                logoPosition={logoPosition}
                stampStoragePath={stampStoragePath}
                stampPosition={stampPosition}
                stampHidden={stampHidden}
              />
            </div>
          </div>
        </TabsContent>

        {/* 公開リンクリストタブ */}
        <TabsContent value="links" className="flex-1 overflow-y-auto p-6">
          <div className="max-w-2xl mx-auto">
            <ConsentLinksTab consentDocumentId={id} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
